import { CommonModule, NgComponentOutlet } from '@angular/common';
import { AfterContentChecked, AfterViewInit, ChangeDetectorRef, Component, ComponentRef, ElementRef, HostListener, Input, OnDestroy, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { GridsterModule } from 'angular-gridster2';
import { DxButtonModule, DxFormModule, DxPopupModule, DxSelectBoxModule, DxTextBoxModule } from 'devextreme-angular';
import { WidgetDataService } from './data/widget-data.service';
import { IBaseWidgetConfig, WidgetComponent, WidgetSettingsComponent, WidgetType, getSettingsComponent, getWidgetComponent, widgetComponentsList } from './base/widget-list';
import { IBaseWidget } from './base/base-widget';
import { DashboardDataService } from '../../data/dashboard-data.service';
import { ValueChangedEvent } from 'devextreme/ui/select_box';
import { custom } from 'devextreme/ui/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject, Subscription, takeUntil } from 'rxjs';

@Component({
  selector: 'app-widget-wrapper',
  standalone: true,
  imports: [GridsterModule, CommonModule, DxPopupModule, DxButtonModule, DxFormModule, DxSelectBoxModule, DxTextBoxModule],
  providers: [WidgetDataService],
  templateUrl: './widget.component.html',
  styleUrl: './widget.component.scss'
})
export class WidgetWrapperComponent implements OnDestroy, AfterViewInit, AfterContentChecked {

  // Componenty pre widget
  public widgetComponent: Type<WidgetComponent> | null = null;
  public setingsComponent: Type<WidgetSettingsComponent> | null = null;

  public widgetComponentsList = widgetComponentsList; 

  @ViewChild('settingsContainer', { read: ViewContainerRef }) container?: ViewContainerRef;
  @ViewChild('widgetContainer', { read: ViewContainerRef }) widgetContainer?: ViewContainerRef;
  @ViewChild('widgetHeader') widgetHeader?: ElementRef;
  @ViewChild('widgetWrapper') widgetWrapper?: ElementRef;

  private _item: IBaseWidget = {} as IBaseWidget;
  
  @Input() set item(item: IBaseWidget) {
    this._item = item;
    this.widgetComponent = getWidgetComponent(item.type);
    this.setingsComponent = getSettingsComponent(item.type);
    this.widgetDataService.setOptions(item.options);
  };

  get item(): IBaseWidget {
    return this._item;
  }

  private readonly _unSubscriber: Subject<any> = new Subject();
  private _supplierStateSub?: Subscription;
  private _settingsComponentRef?: ComponentRef<WidgetSettingsComponent>;
  private _widgetComponentRef?: ComponentRef<WidgetComponent>;

  protected supplierState: boolean | null = null;

  constructor(
    public widgetDataService: WidgetDataService<IBaseWidgetConfig>,
    public dashboardDataService: DashboardDataService,
    private _sanitizer: DomSanitizer,
    private readonly _cd: ChangeDetectorRef
  ){
  }

  @HostListener('window:resize', ['$event'])
  onResize(e: any) {
    setTimeout(() => {
      this._calculateWidgetSize();
    }, 250);
  }

  ngAfterViewInit(): void {
    this.dashboardDataService.edit$.pipe(takeUntil(this._unSubscriber)).subscribe(val => {
      if (!val) {
        setTimeout(() => {
          if (this.widgetComponent) {
            this._widgetComponentRef = this.widgetContainer?.createComponent(this.widgetComponent);

            if (this._widgetComponentRef) {
              this._supplierStateSub?.unsubscribe();
              this._supplierStateSub = this._widgetComponentRef?.instance.supplierState$().pipe(takeUntil(this._unSubscriber)).subscribe(val => {
                this.supplierState = val;
              });
            }
          }

          this._calculateWidgetSize();
        }, 250)
      } else {
        this._widgetComponentRef?.destroy();
        this._widgetComponentRef = undefined;
      }
    })
  }

  private _calculateWidgetSize(): void {
    const height = (this.widgetWrapper?.nativeElement.offsetHeight ?? 0) - (this.widgetHeader?.nativeElement.offsetHeight ?? 0);
    const width = this.widgetWrapper?.nativeElement.offsetWidth ?? 0;

    if (height && width && Number.isInteger(height) && height > 0 && width > 0) {
      this.widgetDataService.parentWidgetResize(width - 30, height - 20)
    }
  }

  ngAfterContentChecked(): void {
    this._cd.detectChanges();
  }

  ngOnDestroy(): void {
    this._unSubscriber.next(null);
    this._unSubscriber.complete();
    this._settingsComponentRef?.destroy();
    this._cd.detach();
  }

  openSettings() {
    this.container?.clear();

    // Vytvor komponent
    if (this.setingsComponent) {
      this._settingsComponentRef = this.container?.createComponent(this.setingsComponent);
      if (this._settingsComponentRef) {
        const opt = this.widgetDataService.getOptions();
        if (opt !== undefined) {
          this._settingsComponentRef.instance.setFormData(opt as any)
        }
        const sub = this._settingsComponentRef.instance.onClose.subscribe(val => {
          if (val) {
            this.item.title = val.title;
            this.item.options = val;
            this.dashboardDataService.updatedashboard();
          }
          
          this._settingsComponentRef?.destroy();
          sub?.unsubscribe();
        });
      }
    }
  }

  changeType(type: ValueChangedEvent) {
    // ak si vyberie novy typ widgetu tak pouzivatela upozornime ze sa stratia vsetky nastavenia
    // a ze su si isty ze chcu pokracovat
    // show dialog
    if (this.item.type !== WidgetType.Empty && this.item.type !== type.value){
      let changeLayoutDialog = custom({
        title: "Change type",
        messageHtml: `<p>You are trying to change widget type.</p>
                      <p>All settings will be lost.</p>
                      <p>Are you sure?</p>`,
        buttons: [
          {
            text: "Ok",
            onClick: (e) => {
                return true;
            }
          }, 
          {
            text: "Cancel",
            onClick: (e) => {
                return false;
            }
          }
        ]
      });

      changeLayoutDialog.show().then((result: boolean) => {
        if (result) {
          this._changeType(type.value);
        } else {
          type.component.option("value", this.item.type);
        }
      });
    } else {
      this._changeType(type.value);
    }
  }

  private _changeType(type: WidgetType) {
    this.item.type = type;
    this.item.options = JSON.parse(JSON.stringify(widgetComponentsList.find(w => w.type === type)?.defaultOptions));
    this.item.title = JSON.parse(JSON.stringify(widgetComponentsList.find(w => w.type === type)?.name ?? 'widget title'));
    this.item.hasRefreshButton = widgetComponentsList.find(w => w.type === type)?.hasRefreshButton ?? false;
    this.widgetDataService.setOptions(this.item.options);
    this.widgetComponent = getWidgetComponent(type);
    this.setingsComponent = getSettingsComponent(type);
    this.dashboardDataService.updatedashboard();
    
    // this.openSettings();
  }

  deleteWidget() {
    // Ak sa snazime zmazat widget tak pouzivatela upozornime ze sa stratia vsetky nastavenia
    // a ze su si isty ze chcu pokracovat
    if (this.item.type !== WidgetType.Empty){
      let changeLayoutDialog = custom({
        title: "Delete widget",
        messageHtml: `<p>You are trying to delete widget.</p>
                      <p>All settings and data will be lost.</p>
                      <p>Are you sure?</p>`,
        buttons: [
          {
            text: "Ok",
            onClick: (e) => {
                return true;
            }
          }, 
          {
            text: "Cancel",
            onClick: (e) => {
                return false;
            }
          }
        ]
      });

      changeLayoutDialog.show().then((result: boolean) => {
        if (result) {
          this.dashboardDataService.deleteWidget(this.item);
        }
      });
    } else {
      this.dashboardDataService.deleteWidget(this.item);
    }
  }

  getSafeSvgIcon(icon: string): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(icon);
  }

  onRefresh(): void {
    if (this._widgetComponentRef?.instance) {
      this._widgetComponentRef.instance.onRefresh();
    }
  }
}